import is from '@sindresorhus/is';
import type { Result } from 'true-myth';

import type { HttpFetcherError } from '~/shared/http-fetcher/http-fetcher';
import type { LanguageCode } from '~/shared/internationalization';
import type { ValidatingHttpFetcherWithBaseUrl } from '~/shared/validating-http-fetcher';

import type {
    AiTransformationAssignLeadsRequest,
    AiTransformationAssignLeadsResponse,
    AiTransformationFiltersResponse,
    AiTransformationInviteLeadRequest,
    AiTransformationItemSort,
    AiTransformationItemsResponse,
    AiTransformationLeadCandidates,
    AiTransformationLeadSearch,
    AiTransformationLocation,
    AiTransformationMatrixGrouping,
    AiTransformationMatrixResponse,
    AiTransformationStatus,
    AiTransformationStatusUpdateRequest,
    AiTransformationStatusUpdateResponse,
    AiTransformationSubject,
    AiTransformationSummaryResponse,
    AiTransformationTreeResponse,
    AiTransformationTreeView,
    AutomationScoreLevel,
} from '../../data-contract/ai-transformation-strategy';
import {
    aiTransformationAssignLeadsResponseSchema,
    aiTransformationFiltersResponseSchema,
    aiTransformationItemsResponseSchema,
    aiTransformationLeadCandidatesSchema,
    aiTransformationLeadSearchSchema,
    aiTransformationLocationSchema,
    aiTransformationMatrixResponseSchema,
    aiTransformationStatusUpdateResponseSchema,
    aiTransformationSummaryResponseSchema,
    aiTransformationTreeResponseSchema,
} from '../../data-contract/ai-transformation-strategy';

/** The bounds of a rectangle on the matrix, all inclusive: score 0..1 by business impact 0..1. */
export type AiTransformationPickBounds = {
    readonly impactMax: number;
    readonly impactMin: number;
    readonly scoreMax: number;
    readonly scoreMin: number;
};

/** A rectangle picked on the matrix at `grouping`. */
export type AiTransformationPick = AiTransformationPickBounds & { readonly grouping: AiTransformationMatrixGrouping };

/**
 * The shared filter query params of every AI transformation read and status update. Empty lists, no `q`, no `pick` and
 * no `treeAttributeId` leave a param out.
 */
export type AiTransformationFilters = {
    /** Options of the filters' attributes, of any of them. */
    readonly attributeValueId: readonly string[];
    readonly jobClusterId: readonly string[];
    readonly jobFamilyId: readonly string[];
    readonly levelId: readonly string[];
    readonly pick?: AiTransformationPick;
    readonly q: string | undefined;
    readonly roleId: readonly string[];
    readonly scoreLevel: readonly AutomationScoreLevel[];
    /**
     * The attribute whose value path groups the POSITION tree, and so the `node` of its items and where `locate` finds
     * a position; the backend's default without it. Only the POSITION tree's reads set it, all of them the same.
     */
    readonly treeAttributeId?: string;
};

type RequestContext = {
    readonly acceptLanguage?: LanguageCode;
    readonly signal?: AbortSignal;
};

type ArchitectureRequest = RequestContext & {
    readonly architectureId: string;
};

export type FetchAiTransformationSummaryInput = ArchitectureRequest & {
    readonly filters: AiTransformationFilters;
    readonly subject: AiTransformationSubject;
};

export type FetchAiTransformationItemsInput = ArchitectureRequest & {
    readonly filters: AiTransformationFilters;
    /** A tree node's opaque key: only the positions sitting directly in it. */
    readonly node?: string;
    readonly page: number;
    readonly size: number;
    readonly sort?: AiTransformationItemSort;
    readonly statuses?: readonly AiTransformationStatus[];
    readonly subject: AiTransformationSubject;
};

export type FetchAiTransformationTreeInput = ArchitectureRequest & {
    readonly filters: AiTransformationFilters;
    readonly view: AiTransformationTreeView;
};

export type FetchAiTransformationLocationInput = ArchitectureRequest & {
    readonly filters: AiTransformationFilters;
    readonly id: string;
    /** The view whose list to find the item in; a position for the tree views, a task for `TASK`. */
    readonly view: AiTransformationMatrixGrouping;
};

export type FetchAiTransformationMatrixInput = ArchitectureRequest & {
    readonly filters: AiTransformationFilters;
    readonly grouping: AiTransformationMatrixGrouping;
};

export type UpdateAiTransformationStatusesInput = {
    readonly architectureId: string;
    /** The page's filters; the response's summary counts under them. */
    readonly filters: AiTransformationFilters;
    readonly request: AiTransformationStatusUpdateRequest;
};

export type FetchAiTransformationLeadCandidatesInput = {
    readonly limit: number;
    readonly searchTerm: string;
    readonly signal?: AbortSignal;
};

export type AssignAiTransformationLeadsInput = {
    readonly architectureId: string;
    readonly request: AiTransformationAssignLeadsRequest;
};

export type InviteAiTransformationLeadInput = {
    readonly architectureId: string;
    readonly inviteLanguage: string;
    readonly request: AiTransformationInviteLeadRequest;
};

export type AiTransformationFetchers = {
    readonly assignAiTransformationLeads: (
        input: AssignAiTransformationLeadsInput,
    ) => Promise<Result<AiTransformationAssignLeadsResponse, HttpFetcherError>>;
    readonly fetchAiTransformationLeadCandidates: (
        input: FetchAiTransformationLeadCandidatesInput,
    ) => Promise<Result<AiTransformationLeadCandidates, HttpFetcherError>>;
    readonly searchAiTransformationLeads: (
        input: FetchAiTransformationLeadCandidatesInput,
    ) => Promise<Result<AiTransformationLeadSearch, HttpFetcherError>>;
    readonly inviteAiTransformationLead: (
        input: InviteAiTransformationLeadInput,
    ) => Promise<Result<AiTransformationAssignLeadsResponse, HttpFetcherError>>;
    readonly fetchAiTransformationFilters: (
        input: ArchitectureRequest,
    ) => Promise<Result<AiTransformationFiltersResponse, HttpFetcherError>>;
    readonly fetchAiTransformationItems: (
        input: FetchAiTransformationItemsInput,
    ) => Promise<Result<AiTransformationItemsResponse, HttpFetcherError>>;
    readonly fetchAiTransformationLocation: (
        input: FetchAiTransformationLocationInput,
    ) => Promise<Result<AiTransformationLocation, HttpFetcherError>>;
    readonly fetchAiTransformationMatrix: (
        input: FetchAiTransformationMatrixInput,
    ) => Promise<Result<AiTransformationMatrixResponse, HttpFetcherError>>;
    readonly fetchAiTransformationSummary: (
        input: FetchAiTransformationSummaryInput,
    ) => Promise<Result<AiTransformationSummaryResponse, HttpFetcherError>>;
    readonly fetchAiTransformationTree: (
        input: FetchAiTransformationTreeInput,
    ) => Promise<Result<AiTransformationTreeResponse, HttpFetcherError>>;
    readonly updateAiTransformationStatuses: (
        input: UpdateAiTransformationStatusesInput,
    ) => Promise<Result<AiTransformationStatusUpdateResponse, HttpFetcherError>>;
};

export type AiTransformationFetchersDependencies = {
    readonly validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl;
};

type SearchParamValue = number | string | readonly string[] | undefined;

/** Writes repeatable params once per value and leaves out empty ones, as the backend reads them. */
export const aiTransformationSearchParams = (params: Readonly<Record<string, SearchParamValue>>): URLSearchParams => {
    const searchParams = new URLSearchParams();

    for (const [name, value] of Object.entries(params)) {
        for (const entry of is.array(value) ? value : [value]) {
            if (entry !== undefined && entry !== '') searchParams.append(name, String(entry));
        }
    }

    return searchParams;
};

const filterParams = ({ pick, ...filters }: AiTransformationFilters): Readonly<Record<string, SearchParamValue>> => ({
    ...filters,
    pickGrouping: pick?.grouping,
    pickImpactMax: pick?.impactMax,
    pickImpactMin: pick?.impactMin,
    pickScoreMax: pick?.scoreMax,
    pickScoreMin: pick?.scoreMin,
});

const pathOf = (architectureId: string, endpoint: string) =>
    `/ai-transformation/architectures/${architectureId}/${endpoint}` as const;

const requestOptions = ({ acceptLanguage, signal }: RequestContext, searchParams?: URLSearchParams) => ({
    headers: acceptLanguage === undefined ? undefined : { 'Accept-Language': acceptLanguage },
    searchParams,
    signal,
});

export const createAiTransformationFetchers = ({
    validatingCobrainerApiFetcher,
}: AiTransformationFetchersDependencies): AiTransformationFetchers => ({
    assignAiTransformationLeads: ({ architectureId, request }) =>
        validatingCobrainerApiFetcher.putJson(aiTransformationAssignLeadsResponseSchema, {
            pathname: pathOf(architectureId, 'transformation-leads'),
            payload: request,
        }),
    inviteAiTransformationLead: ({ architectureId, inviteLanguage, request }) =>
        validatingCobrainerApiFetcher.postJson(aiTransformationAssignLeadsResponseSchema, {
            pathname: pathOf(architectureId, 'transformation-leads/invite'),
            payload: request,
            requestOptions: { searchParams: new URLSearchParams({ inviteLanguage }) },
        }),
    searchAiTransformationLeads: ({ limit, searchTerm, signal }) =>
        validatingCobrainerApiFetcher.get(aiTransformationLeadSearchSchema, {
            pathname: '/ai-transformation/transformation-leads',
            requestOptions: { searchParams: { limit, search_term: searchTerm }, signal },
        }),
    fetchAiTransformationLeadCandidates: ({ limit, searchTerm, signal }) =>
        validatingCobrainerApiFetcher.get(aiTransformationLeadCandidatesSchema, {
            pathname: '/ai-transformation/transformation-leads/candidates',
            requestOptions: { searchParams: { limit, search_term: searchTerm }, signal },
        }),
    fetchAiTransformationFilters: ({ architectureId, ...context }) =>
        validatingCobrainerApiFetcher.get(aiTransformationFiltersResponseSchema, {
            pathname: pathOf(architectureId, 'filters'),
            requestOptions: requestOptions(context),
        }),
    fetchAiTransformationItems: ({ architectureId, filters, node, page, size, sort, statuses, subject, ...context }) =>
        validatingCobrainerApiFetcher.get(aiTransformationItemsResponseSchema, {
            pathname: pathOf(architectureId, 'items'),
            requestOptions: requestOptions(
                context,
                aiTransformationSearchParams({
                    subject,
                    ...filterParams(filters),
                    node,
                    page,
                    size,
                    sort,
                    status: statuses,
                }),
            ),
        }),
    fetchAiTransformationLocation: ({ architectureId, filters, id, view, ...context }) =>
        validatingCobrainerApiFetcher.get(aiTransformationLocationSchema, {
            pathname: pathOf(architectureId, 'locate'),
            requestOptions: requestOptions(
                context,
                aiTransformationSearchParams({ id, view, ...filterParams(filters) }),
            ),
        }),
    fetchAiTransformationMatrix: ({ architectureId, filters, grouping, ...context }) =>
        validatingCobrainerApiFetcher.get(aiTransformationMatrixResponseSchema, {
            pathname: pathOf(architectureId, 'matrix'),
            requestOptions: requestOptions(
                context,
                aiTransformationSearchParams({ grouping, ...filterParams(filters) }),
            ),
        }),
    fetchAiTransformationSummary: ({ architectureId, filters, subject, ...context }) =>
        validatingCobrainerApiFetcher.get(aiTransformationSummaryResponseSchema, {
            pathname: pathOf(architectureId, 'summary'),
            requestOptions: requestOptions(
                context,
                aiTransformationSearchParams({ subject, ...filterParams(filters) }),
            ),
        }),
    fetchAiTransformationTree: ({ architectureId, filters, view, ...context }) =>
        validatingCobrainerApiFetcher.get(aiTransformationTreeResponseSchema, {
            pathname: pathOf(architectureId, 'tree'),
            requestOptions: requestOptions(context, aiTransformationSearchParams({ view, ...filterParams(filters) })),
        }),
    updateAiTransformationStatuses: ({ architectureId, filters, request }) =>
        validatingCobrainerApiFetcher.putJson(aiTransformationStatusUpdateResponseSchema, {
            pathname: pathOf(architectureId, 'statuses'),
            payload: request,
            requestOptions: { searchParams: aiTransformationSearchParams(filterParams(filters)) },
        }),
});
